import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import Img from '../../../../Img';
import { getFlagUrl } from '../../../../../utils';
import {
    VERIFIED_ICON_SRC,
    UTR_ICON_SRC,
    LOCK_ICON_SRC,
    LOCKED_PLACEHOLDER_INSIGHT,
    LOCKED_PLACEHOLDER_IMPACT_WEIGHT,
    MAX_GROUPED_TOOLTIP_CARDS,
    lockHandlerPropType,
} from '../performanceStatsUtils';
import { insightBannerClass, insightBannerText, impactSentence } from '../performanceInsight';
import { ImpactInfo } from '../ScatterSection/scatterInfo';
import usePerformanceStatsEngagement from '../usePerformanceStatsEngagement';
import useIsOwnProfile from '../../../../hooks/useIsOwnProfile';
import { performanceStatsEngagementTypes } from '../../../../../utils/analytics';
import { ratingTypes } from '../../../../../utils/constants';
import styles from '../performanceStats.scss';

// `classes` is the caller's own CSS module — the Stats tab and the Overview teaser
// render the same row with different spacing.
export function PlayerRow({ player, sets, isOpponent, isWinner, classes, 'data-testid': dataTestId }) {
    const trackEngagement = usePerformanceStatsEngagement();
    if (!player) { return null; }
    const nameClassName = `${classes.playerName} ${(isOpponent && classes.playerNameOpponent) || ''}`;
    const showProfileLink = isOpponent && player.playerId != null;
    return (
        <div className={classes.playerRow} data-testid={dataTestId}>
            {player.nationality && (
                <Img src={getFlagUrl(player.nationality)} alt={player.nationality} className={classes.playerFlag} data-testid={dataTestId && `${dataTestId}.flag`} />
            )}
            {showProfileLink ? (
                <Link
                    className={classes.opponentNameLink || nameClassName}
                    to={`/profiles/${player.playerId}`}
                    onClick={() => trackEngagement(performanceStatsEngagementTypes.GO_TO_OTHER_PROFILE, player.playerId)}
                    data-testid={dataTestId && `${dataTestId}.name`}
                >
                    {player.name}
                </Link>
            ) : (
                <span className={`${classes.playerName} ${(isOpponent && classes.playerNameOpponent) || ''}`} data-testid={dataTestId && `${dataTestId}.name`}>{player.name}</span>
            )}
            {player.rating && (
                <span className={classes.playerRating} data-testid={dataTestId && `${dataTestId}.rating`}>{player.rating}</span>
            )}
            <span className={classes.playerScores} data-testid={dataTestId && `${dataTestId}.scores`}>
                {sets.map((set, setIndex) => {
                    const games = isOpponent ? set.opponent : set.me;
                    const tiebreak = isOpponent ? set.opponentTiebreak : set.meTiebreak;
                    const won = isOpponent ? set.opponent > set.me : set.me > set.opponent;
                    return (
                        <span
                            key={`set-${setIndex}-${set.me}-${set.opponent}`}
                            className={`${classes.setScore} ${won ? classes.setScoreWon : ''}`}
                            data-testid={dataTestId && `${dataTestId}.scores.set-${setIndex}`}
                        >
                            {games}
                            {tiebreak != null && <sup data-testid={dataTestId && `${dataTestId}.scores.set-${setIndex}.tiebreak`}>{tiebreak}</sup>}
                        </span>
                    );
                })}
            </span>
            {isWinner && <span className={classes.winnerCaret} data-testid={dataTestId && `${dataTestId}.winner-indicator`} />}
        </div>
    );
}

PlayerRow.propTypes = {
    player: PropTypes.shape({
        playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        nationality: PropTypes.string,
        rating: PropTypes.string,
    }),
    sets: PropTypes.arrayOf(PropTypes.shape({
        me: PropTypes.number,
        opponent: PropTypes.number,
        meTiebreak: PropTypes.number,
        opponentTiebreak: PropTypes.number,
    })),
    isOpponent: PropTypes.bool,
    isWinner: PropTypes.bool,
    classes: PropTypes.objectOf(PropTypes.string),
    'data-testid': PropTypes.string,
};

PlayerRow.defaultProps = {
    player: null,
    sets: [],
    isOpponent: false,
    isWinner: false,
    classes: styles,
    'data-testid': null,
};

// `classes` is the caller's own CSS module — the Stats tab, the Overview teaser and
// the scatter tooltip render the same icon pair with different spacing.
export function VerifiedUtrIcons({ classes, 'data-testid': dataTestId }) {
    return (
        <React.Fragment>
            <span className={classes.headerIconDivider} />
            <Img
                src={VERIFIED_ICON_SRC}
                alt="Verified"
                title="This result is from a verified event"
                className={classes.headerIcon}
                data-testid={dataTestId && `${dataTestId}.verified-icon`}
            />
            <span className={classes.headerIconDivider} />
            <Img
                src={UTR_ICON_SRC}
                alt={ratingTypes.tennis}
                title={`Used for ${ratingTypes.tennis} Calculation`}
                className={classes.headerIcon}
                data-testid={dataTestId && `${dataTestId}.utr-icon`}
            />
        </React.Fragment>
    );
}

VerifiedUtrIcons.propTypes = {
    classes: PropTypes.objectOf(PropTypes.string),
    'data-testid': PropTypes.string,
};

VerifiedUtrIcons.defaultProps = {
    classes: styles,
    'data-testid': null,
};

const resolveInsight = (card, locked) => {
    return {
        insight: card.insight || (locked ? LOCKED_PLACEHOLDER_INSIGHT : null),
        impactWeight: card.impactWeight || (locked ? LOCKED_PLACEHOLDER_IMPACT_WEIGHT : null),
    };
};

export const hasCardInsight = (card, locked) => {
    const { insight, impactWeight } = resolveInsight(card, locked);
    return insight != null || impactWeight != null;
};

export function InsightBlock({ card, locked, onLockClick, classes, 'data-testid': dataTestId }) {
    const isOwnProfile = useIsOwnProfile();
    const { insight, impactWeight } = resolveInsight(card, locked);
    if (insight == null && impactWeight == null) { return null; }
    return (
        <div
            className={`${classes.insightBlock} ${(locked && classes.insightBlockLocked) || ''}`}
            data-testid={dataTestId && `${dataTestId}.insight-block`}
        >
            {insight && (
                <div
                    className={`${classes.insightBanner} ${insightBannerClass(insight.direction, classes)}`}
                    aria-hidden={locked || undefined}
                    data-testid={dataTestId && `${dataTestId}.insight`}
                >
                    {insightBannerText(insight, classes, dataTestId && `${dataTestId}.insight`, isOwnProfile)}
                </div>
            )}
            {impactWeight && (
                <div className={classes.impactSentence} aria-hidden={locked || undefined} data-testid={dataTestId && `${dataTestId}.impact`}>
                    {impactSentence(impactWeight, dataTestId && `${dataTestId}.impact`, isOwnProfile)}
                    {!locked && <ImpactInfo isOwnProfile={isOwnProfile} data-testid={dataTestId && `${dataTestId}.impact`} />}
                </div>
            )}
            {locked && (
                <button
                    type="button"
                    className={classes.insightLockBtn}
                    onClick={onLockClick}
                    aria-label="Upgrade to Power to see match performance"
                    data-testid={dataTestId && `${dataTestId}.insight-lock.btn`}
                >
                    <Img src={LOCK_ICON_SRC} alt="locked" className={classes.insightLockIcon} data-testid={dataTestId && `${dataTestId}.insight-lock.icon`} />
                </button>
            )}
        </div>
    );
}

InsightBlock.propTypes = {
    card: PropTypes.shape({
        insight: PropTypes.shape({
            percentLabel: PropTypes.string,
            direction: PropTypes.string,
        }),
        impactWeight: PropTypes.string,
    }).isRequired,
    locked: PropTypes.bool,
    onLockClick: lockHandlerPropType,
    classes: PropTypes.objectOf(PropTypes.string),
    'data-testid': PropTypes.string,
};

InsightBlock.defaultProps = {
    locked: false,
    onLockClick: null,
    classes: styles,
    'data-testid': null,
};

export function GameCardBody({ card, hideImpactInfo, 'data-testid': dataTestId }) {
    const isOwnProfile = useIsOwnProfile();
    if (!card) { return null; }
    const { insight, match } = card;
    return (
        <React.Fragment>
            <div className={styles.gcDate} data-testid={dataTestId && `${dataTestId}.date`}>
                {card.shortDateLabel}
                <VerifiedUtrIcons classes={styles} data-testid={dataTestId} />
            </div>
            {match && (
                <React.Fragment>
                    <PlayerRow player={match.me} sets={match.sets} isWinner={match.profileIsWinner} data-testid={dataTestId && `${dataTestId}.player`} />
                    <PlayerRow player={match.opponent} sets={match.sets} isOpponent isWinner={!match.profileIsWinner} data-testid={dataTestId && `${dataTestId}.opponent`} />
                </React.Fragment>
            )}
            {insight && (
                <div className={`${styles.insightBanner} ${insightBannerClass(insight.direction, styles)}`} data-testid={dataTestId && `${dataTestId}.performance`}>
                    {insightBannerText(insight, styles, dataTestId && `${dataTestId}.performance`, isOwnProfile)}
                </div>
            )}
            {card.impactWeight && (
                <div className={styles.impactSentence} data-testid={dataTestId && `${dataTestId}.impact`}>
                    {impactSentence(card.impactWeight, dataTestId && `${dataTestId}.impact`, isOwnProfile)}
                    {!hideImpactInfo && <ImpactInfo isOwnProfile={isOwnProfile} data-testid={dataTestId && `${dataTestId}.impact`} />}
                </div>
            )}
        </React.Fragment>
    );
}

const cardShape = PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shortDateLabel: PropTypes.string,
    insight: PropTypes.shape({
        percentLabel: PropTypes.string,
        direction: PropTypes.string,
    }),
    impactWeight: PropTypes.string,
    match: PropTypes.shape({
        me: PropTypes.shape({}),
        opponent: PropTypes.shape({}),
        sets: PropTypes.arrayOf(PropTypes.shape({})),
    }),
});

GameCardBody.propTypes = {
    card: cardShape,
    hideImpactInfo: PropTypes.bool,
    'data-testid': PropTypes.string,
};

GameCardBody.defaultProps = {
    card: null,
    hideImpactInfo: false,
    'data-testid': null,
};

// Overlapping dots hide each other, so one hover can carry several matches.
const GameCardTooltip = React.forwardRef(({ cards, position, 'data-testid': dataTestId }, ref) => {
    if (!cards?.length) { return null; }
    if (cards.length === 1) {
        return (
            <div ref={ref} className={styles.gameCard} style={position} data-testid={dataTestId}>
                <GameCardBody card={cards[0]} hideImpactInfo data-testid={dataTestId} />
            </div>
        );
    }
    const visibleCards = cards.slice(0, MAX_GROUPED_TOOLTIP_CARDS);
    const hiddenCount = cards.length - visibleCards.length;
    return (
        <div ref={ref} className={`${styles.gameCard} ${styles.gameCardGrouped}`} style={position} data-testid={dataTestId}>
            {visibleCards.map((card, cardIndex) => {
                const cardKey = card.id ?? cardIndex;
                const cardTestId = dataTestId && `${dataTestId}.card-${cardKey}`;
                return (
                    <div key={cardKey} className={styles.gameCardGroupItem} data-testid={cardTestId}>
                        <GameCardBody card={card} hideImpactInfo data-testid={cardTestId} />
                    </div>
                );
            })}
            {hiddenCount > 0 && (
                <div className={styles.gameCardMore} data-testid={dataTestId && `${dataTestId}.more`}>
                    {`+${hiddenCount} more`}
                </div>
            )}
        </div>
    );
});

GameCardTooltip.displayName = 'GameCardTooltip';

GameCardTooltip.propTypes = {
    cards: PropTypes.arrayOf(cardShape),
    position: PropTypes.shape({}).isRequired,
    'data-testid': PropTypes.string,
};

GameCardTooltip.defaultProps = {
    cards: [],
    'data-testid': null,
};

export default GameCardTooltip;
